import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import MovieCard from '@/components/feature/MovieCard';
import PlayModal from './components/PlayModal';
import { formatViews, getMovieById, getRelatedMovies } from '@/utils/movie';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TYPE_LABELS } from '@/mocks/movies';

export default function DetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const [playOpen, setPlayOpen] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta(
    movie ? `${movie.title} - 详细介绍与在线观看` : '影片详情 - 亚洲中文字幕在线观看',
    movie ? `${movie.title}：${movie.synopsis.slice(0, 80)}` : undefined,
  );

  if (!movie) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-8 py-16">
          <EmptyState
            icon="ri-movie-2-line"
            title="没有找到这部电影"
            description="该影片可能已下架或链接有误，返回首页看看其他精彩内容吧。"
            actionLabel="返回首页"
            onAction={() => navigate('/')}
          />
        </div>
      </Layout>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const favorited = isFavorite(movie.id);
  const typeLabel = TYPE_LABELS[movie.type] ?? '影视';

  const infoItems = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.actors.join(' / ') },
    { label: '地区', value: movie.region },
    { label: '年份', value: `${movie.year}` },
    { label: '时长', value: movie.duration },
    { label: '类型', value: movie.genres.join(' / ') },
  ];

  return (
    <Layout>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: typeLabel, to: `/category/${movie.type}` },
            { label: movie.title },
          ]}
        />

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-100 border border-background-200 group">
              <img
                src={movie.backdrop || movie.cover}
                alt={`${movie.title} 主图`}
                title={`${movie.title} - 亚洲中文字幕在线观看`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-background-950/50 flex flex-col items-center justify-center transition-colors group-hover:bg-background-950/40">
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-background-950 hover:bg-primary-600 cursor-pointer transition-colors"
                  aria-label="播放"
                >
                  <i className="ri-play-fill text-3xl md:text-4xl"></i>
                </button>
                <p className="mt-3 text-xs text-background-50/90 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  在线观看视频区域（功能演示）
                </p>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
                  {movie.title}
                </h1>
                <span className="px-2.5 h-6 flex items-center rounded-md bg-primary-500 text-background-950 text-xs font-medium">
                  {typeLabel}
                </span>
                <span className="px-2.5 h-6 flex items-center rounded-md bg-accent-500 text-background-50 text-xs font-label tracking-wide">
                  <i className="ri-star-fill mr-1 text-[10px]"></i>
                  {movie.rating.toFixed(1)}
                </span>
              </div>
              <p className="mt-1 text-sm text-foreground-500 font-label tracking-wide">
                {movie.originalTitle}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line text-accent-500"></i>
                  {formatViews(movie.views)} 次播放
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-primary-500"></i>
                  {movie.year} 年
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-primary-500"></i>
                  {movie.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-earth-line text-primary-500"></i>
                  {movie.region}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 h-6 flex items-center rounded-md bg-secondary-900 text-secondary-200 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h2 className="mt-7 font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-book-open-line text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-8">{movie.synopsis}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="px-6 h-11 flex items-center gap-2 rounded-md bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-play-fill text-lg"></i>
                  模拟播放
                </button>
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`px-6 h-11 flex items-center gap-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap border transition-colors ${
                    favorited
                      ? 'bg-accent-500 border-accent-500 text-background-50'
                      : 'border-background-300 text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <Link
                  to={`/category/${movie.type}`}
                  className="px-6 h-11 flex items-center gap-2 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-arrow-go-back-line"></i>
                  返回{typeLabel}
                </Link>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg bg-background-100 border border-background-200 overflow-hidden">
              <div className="flex gap-4 p-5">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 封面`}
                  title={`${movie.title} - 亚洲中文字幕在线观看`}
                  className="w-24 h-36 rounded-md object-cover object-top shrink-0"
                />
                <div className="min-w-0">
                  <h2 className="font-heading font-bold text-base text-foreground-950 truncate">
                    {movie.title}
                  </h2>
                  <p className="mt-2 text-xs text-foreground-500 leading-6">
                    {movie.synopsis.slice(0, 54)}…
                  </p>
                </div>
              </div>
              <dl className="px-5 pb-2">
                {infoItems.map((item) => (
                  <div
                    key={item.label}
                    className="flex gap-3 py-2.5 border-t border-background-200/70 text-sm"
                  >
                    <dt className="w-12 shrink-0 text-foreground-500">{item.label}</dt>
                    <dd className="text-foreground-800 flex-1">{item.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="p-5 pt-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`w-full h-10 flex items-center justify-center gap-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    favorited
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-primary-500 text-background-950 hover:bg-primary-600'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorited ? '已收藏到本地' : '收藏到本地'}
                </button>
              </div>
            </div>

            <div className="mt-5 rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-magic-line text-primary-500"></i>
                相关推荐
              </h2>
              <div className="space-y-3">
                {related.slice(0, 4).map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex gap-3 cursor-pointer"
                  >
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      title={`${item.title} - 亚洲中文字幕在线观看`}
                      className="w-14 h-20 rounded-md object-cover object-top shrink-0"
                    />
                    <div className="min-w-0 flex-1 py-1">
                      <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500 truncate">
                        {item.region} · {item.year}
                      </p>
                      <p className="mt-1 text-xs text-foreground-500 flex items-center gap-1">
                        <i className="ri-star-fill text-primary-500 text-[10px]"></i>
                        {item.rating.toFixed(1)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-10">
          <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5 flex items-center gap-2">
            <i className="ri-thumb-up-line text-primary-500"></i>
            猜你也会喜欢
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} showFavorite={false} />
            ))}
          </div>
        </section>
      </div>

      {playOpen && (
        <PlayModal
          title={movie.title}
          cover={movie.backdrop || movie.cover}
          duration={movie.duration}
          onClose={() => setPlayOpen(false)}
        />
      )}
    </Layout>
  );
}